import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import Iconify from '@/components/iconify';

import InfiniteScroll from 'react-infinite-scroller';
import { useNavigate } from 'react-router-dom';

import { api } from '@/api/api-client.config';
import PickemDialog from '@/components/pickem-admin/PickemDialog';
import Label from '@/components/label';
import { Pickem } from '@/@types/api.types.ts';
import { UpdateGamesListButton } from '@/components/pickem-admin/UpdateGamesListButton.tsx';
import { useGetInfinitePickems } from '@/api/pickems/getPickems.ts';
import { useCreatePickemMutation } from '@/api/pickems/createPickem.ts';
import { formatDateWithTimezone } from 'src/components/Timezone/helpers';
import { useTimezoneStore } from 'src/store/timezoneStore';
import { PickemStatusEnum, PickemTypeEnum } from 'src/enums/Pickem.enums';

interface TableHeaderProps {
  label: string;
  key: string;
  align: 'left' | 'right' | 'center' | 'inherit' | 'justify';
}

export default function GamePage() {
  const navigate = useNavigate();
  const [open, setOpen] = useState<Element | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [openCreatePickemDialog, setOpenCreatePickemDialog] =
    useState<boolean>(false);

  const { timezone } = useTimezoneStore();

  const {
    data: pickems = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetInfinitePickems({
    limit: 10,
  });

  const { mutateAsync: createPickem } = useCreatePickemMutation();

  const handleOpenMenu = (event: any) => {
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };

  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };

  const duplicatePickemHandler = () => {
    setOpen(null);
    api
      .post(`/admin/pickems/${openId}/duplicate`)
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  // Create Pickem
  const createPickemHandler = (pickem: Pickem) => {
    setOpenCreatePickemDialog(false);
    createPickem(pickem)
      .then(() => window.location.reload())
      .catch((error) => {
        console.error('Error:', error);
      });
  };

  const closeCreatePickemDialog = () => {
    setOpenCreatePickemDialog(false);
  };

  const headers: TableHeaderProps[] = [
    { label: 'ID', key: 'pickem_id', align: 'left' },
    { label: 'Title', key: 'title_en', align: 'left' },
    { label: 'Status', key: 'status', align: 'center' },
    { label: 'Tenants', key: 'tenants', align: 'left' },
    { label: `Start (${timezone})`, key: 'start', align: 'left' },
    { label: '', key: 'actions', align: 'center' },
  ];

  return (
    <>
      <Helmet>
        <title> Pickems </title>
      </Helmet>

      <Container>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          mb={5}
        >
          <Typography variant='h4' gutterBottom>
            Pickems
          </Typography>
          <Box>
            <UpdateGamesListButton />
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => setOpenCreatePickemDialog(true)}
            >
              New Pickem
            </Button>
          </Box>
        </Stack>

        <Card>
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <InfiniteScroll
              pageStart={0}
              loadMore={() => fetchNextPage()}
              hasMore={hasNextPage}
              loader={<Skeleton height='70px' />}
            >
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell key={header.key} align={header.align}>
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pickems.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell component='th' scope='row' width={20}>
                          {row.pickem_id}
                        </TableCell>
                        <TableCell component='th' scope='row'>
                          <Stack direction='row' alignItems='center' gap={1}>
                            {row.title_en}
                            {row.pickem_type === PickemTypeEnum.PRIVATE && (
                              <Chip
                                size='small'
                                label='Private'
                                color='error'
                                variant='outlined'
                                icon={
                                  <Iconify icon='eva:lock-outline' size={16} />
                                }
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell component='th' scope='row' align='center'>
                          <Chip
                            label={row.status}
                            color={
                              row.status === PickemStatusEnum.COMPLETE
                                ? 'success'
                                : row.status === PickemStatusEnum.IN_PLAY ||
                                  row.status === PickemStatusEnum.UPCOMING
                                ? 'primary'
                                : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell component='th' scope='row' width={200}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {row.tenants.map((tenant: string) => (
                              <Label key={tenant}>{tenant ?? ''}</Label>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell component='th' scope='row' width={180}>
                          {formatDateWithTimezone(row.start, timezone)}
                        </TableCell>

                        <TableCell align='right'>
                          <IconButton
                            size='large'
                            color='inherit'
                            onClick={handleOpenMenu}
                            id={String(row.pickem_id)}
                          >
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </InfiniteScroll>
          )}
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem
          onClick={() =>
            navigate(`/dashboard/leaderboards/view?pickem_id=${openId}`)
          }
        >
          <Iconify icon={'eva:list-outline'} sx={{ mr: 1 }} />
          Leaderboard
        </MenuItem>
        <MenuItem onClick={() => duplicatePickemHandler()}>
          <Iconify icon={'eva:copy-outline'} sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => navigate(`/dashboard/pickem/edit?pickem_id=${openId}`)}
        >
          <Iconify icon={'eva:edit-outline'} sx={{ mr: 1 }} />
          Edit
        </MenuItem>
      </Popover>

      <PickemDialog
        open={openCreatePickemDialog}
        onSuccessHandler={createPickemHandler}
        onClose={closeCreatePickemDialog}
      />
    </>
  );
}
